import { useT } from "@/i18n/I18nProvider";
import { SEO } from "@/components/common/SEO";
import { PageHero } from "@/components/common/PageHero";
import { COMPANY } from "@/data/company";
import { LEGAL_CONTENT } from "@/data/legalContent";
import type { RouteKey } from "@/lib/routes";

/**
 * Legal pages template.
 * Company identification uses verified official data (company.ts).
 * Page-specific legal copy comes from the legalContent.ts registry when
 * present; otherwise the shared "pending final legal review" fallback is
 * shown.
 */
export default function LegalPage({ routeKey }: { routeKey: RouteKey }) {
  const { t, locale } = useT();
  const en = locale === "en";
  const content = LEGAL_CONTENT[routeKey]?.[locale];

  const idRows: Array<[string, string]> = [
    [en ? "Legal name" : "Denominazione", COMPANY.legalName],
    [
      en ? "Registered office" : "Sede legale",
      `${COMPANY.address.street}, ${COMPANY.address.city}, ${COMPANY.address.country[locale]}`,
    ],
    ["P. IVA / C.F.", COMPANY.vat],
    ["REA", `${COMPANY.rea} — ${COMPANY.registeredWith}`],
    ["Email", COMPANY.email],
    ["PEC", COMPANY.pec],
  ];

  return (
    <>
      <SEO routeKey={routeKey} />
      <PageHero routeKey={routeKey} compact />

      <section className="container-bnm py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <aside className="lg:col-span-4">
            <p className="eyebrow">{(t.routes[routeKey] as { eyebrow?: string }).eyebrow}</p>
            <p className="mt-3 font-display text-xl text-ink">
              {t.routes[routeKey].title}
            </p>
          </aside>
          <div className="lg:col-span-8 flex flex-col gap-8">
            <div
              className="border border-bone-500 bg-bone-300/40 p-6 md:p-8"
              data-testid={`legal-company-id-${routeKey}`}
            >
              <p className="eyebrow mb-5">
                {en ? "Company identification" : "Identificazione societaria"}
              </p>
              <dl className="flex flex-col">
                {idRows.map(([label, value]) => (
                  <div
                    key={label}
                    className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-6 border-b border-bone-500 last:border-b-0 py-2.5"
                  >
                    <dt className="font-mono text-[11px] uppercase tracking-eyebrow text-slate2 shrink-0">
                      {label}
                    </dt>
                    <dd className="text-ink text-sm sm:text-right">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {content ? (
              <div className="flex flex-col gap-10" data-testid={`legal-content-${routeKey}`}>
                {content.note && (
                  <div className="border-l-2 border-clay pl-6 py-3" role="note">
                    <p className="text-sm text-slate2 leading-relaxed max-w-prose">
                      {content.note}
                    </p>
                  </div>
                )}
                {content.sections.map((section) => (
                  <div key={section.heading}>
                    <p className="eyebrow mb-4">{section.heading}</p>
                    <div className="flex flex-col gap-4 max-w-prose">
                      {section.paragraphs.map((p, idx) => (
                        <p key={idx} className="text-slate2 leading-relaxed text-[15px]">
                          {p}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div
                  className="border-l-2 border-clay pl-6 py-3"
                  role="note"
                  data-testid={`legal-pending-${routeKey}`}
                >
                  <p className="eyebrow mb-2 !text-clay">
                    {en ? "Status" : "Stato"}
                  </p>
                  <p className="text-ink text-lg md:text-xl font-display font-light leading-snug">
                    {t.sections.legalReviewPending}
                  </p>
                  <p className="mt-2 text-sm text-slate2 max-w-prose-tight">
                    {t.sections.legalReviewPendingHint}
                  </p>
                </div>
                <p className="text-xs text-slate2 font-mono uppercase tracking-eyebrow">
                  {t.sections.contentPendingHint}
                </p>
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
